import React from "react";
import {
	Box,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Slider,
	Switch,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import ClearIcon from "@material-ui/icons/Clear";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import PaletteIcon from "@material-ui/icons/Palette";
import ImageIcon from "@material-ui/icons/Image";
import ErEditAccordion from "./ErEditAccordion";
import TextEffectPicker from "./TextEffectPicker";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";
import { DraftIntTextField } from "./escapeRoomEditorFloatingWidgets";
import { pickEffectBundle, mergeEffectBundlePatch } from "./textEffectStyles";
import { getDefaultHoverColor, normalizeColorToRgba } from "./escapeRoomEditorQuizVisual";
import { BUTTON_ICON_MAP } from "./buttonIconLibrary";
import { ADD_FONT_OPTION } from "./escapeRoomEditorUiConstants";
import ButtonImageVisual, { isButtonImageMode, resolveButtonImageUrl } from "./ButtonImageVisual";
import { getButtonBorderRadiusPercent } from "./buttonBorderRadius";

export default function ButtonElementStylePanel({
	selectedElement,
	effectiveUpdateElement,
	allFontOptions,
	fontLabel,
	onOpenFontManager,
	onOpenIconPicker,
	onOpenButtonImagePicker,
}) {
	const el = selectedElement;
	const style = el.style || {};
	const content = el.content || {};
	const imageMode = isButtonImageMode(content);

	const patchTextEffect = (effectPatch) => {
		effectiveUpdateElement({
			...el,
			style: mergeEffectBundlePatch(style, "text", effectPatch),
		});
	};

	const patchIconEffect = (effectPatch) => {
		effectiveUpdateElement({
			...el,
			content: mergeEffectBundlePatch(content, "icon", effectPatch),
		});
	};

	const patchFrameEffect = (effectPatch) => {
		effectiveUpdateElement({
			...el,
			style: mergeEffectBundlePatch(style, "frame", effectPatch),
		});
	};

	const hasIcon = !!(content.icon || "").trim() && BUTTON_ICON_MAP[content.icon];

	return (
		<>
			<ErEditAccordion title="Imagen del botón" icon={ImageIcon} defaultOpen={imageMode}>
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} p={1.25} borderRadius={10} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
					<Box>
						<Box fontSize={13} fontWeight={600} color="#334155">Usar imagen</Box>
						<Box fontSize={11} color="#64748b">Sustituye texto, icono y fondo por una imagen</Box>
					</Box>
					<Switch
						checked={imageMode}
						onChange={(e) => effectiveUpdateElement({
							...el,
							content: {
								...content,
								buttonUseImage: e.target.checked,
								...(e.target.checked && !content.buttonImageHoverMode ? { buttonImageHoverMode: "multiply" } : {}),
							},
						})}
						color="primary"
					/>
				</Box>
				{imageMode ? (
					<Box display="flex" flexDirection="column" style={{ gap: 14 }}>
						<Box>
							<Box fontSize={12} fontWeight={600} color="#64748b" mb={0.75}>Imagen</Box>
							<Box
								onClick={() => onOpenButtonImagePicker?.("main")}
								display="flex"
								alignItems="center"
								style={{ gap: 12, padding: 12, borderRadius: 10, border: "2px dashed #cbd5e1", background: "#fff", cursor: "pointer" }}
							>
								<Box style={{ width: 72, height: 52, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "#f1f5f9" }}>
									{content.buttonImageUrl ? (
										<img src={resolveButtonImageUrl(content.buttonImageUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
									) : (
										<Box display="flex" alignItems="center" justifyContent="center" height="100%"><ImageIcon style={{ color: "#94a3b8" }} /></Box>
									)}
								</Box>
								<Box flex={1} minWidth={0}>
									<Box fontSize={13} fontWeight={600} color="#1e293b">{content.buttonImageUrl ? "Imagen asignada" : "Elegir imagen"}</Box>
									<Box fontSize={11} color="#64748b">Clic para cambiar desde multimedia</Box>
								</Box>
							</Box>
						</Box>
						<Box>
							<Box fontSize={12} fontWeight={600} color="#64748b" mb={0.75}>Hover</Box>
							<ToggleButtonGroup
								size="small"
								exclusive
								value={content.buttonImageHoverMode === "swap" ? "swap" : "multiply"}
								onChange={(_, val) => {
									if (val == null) return;
									effectiveUpdateElement({ ...el, content: { ...content, buttonImageHoverMode: val } });
								}}
								style={{ marginBottom: 10 }}
							>
								<ToggleButton value="multiply" style={{ textTransform: "none" }}>Color (multiplicar)</ToggleButton>
								<ToggleButton value="swap" style={{ textTransform: "none" }}>Otra imagen</ToggleButton>
							</ToggleButtonGroup>
							{(content.buttonImageHoverMode === "swap") ? (
								<Box
									onClick={() => onOpenButtonImagePicker?.("hover")}
									display="flex"
									alignItems="center"
									style={{ gap: 12, padding: 12, borderRadius: 10, border: "2px dashed #cbd5e1", background: "#fff", cursor: "pointer" }}
								>
									<Box style={{ width: 72, height: 52, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: "#f1f5f9" }}>
										{content.buttonHoverImageUrl ? (
											<img src={resolveButtonImageUrl(content.buttonHoverImageUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
										) : (
											<Box display="flex" alignItems="center" justifyContent="center" height="100%"><ImageIcon style={{ color: "#94a3b8" }} /></Box>
										)}
									</Box>
									<Box flex={1} minWidth={0}>
										<Box fontSize={13} fontWeight={600} color="#1e293b">{content.buttonHoverImageUrl ? "Imagen hover" : "Elegir imagen hover"}</Box>
										<Box fontSize={11} color="#64748b">Se muestra al pasar el ratón</Box>
									</Box>
								</Box>
							) : (
								<Box display="flex" flexDirection="column" style={{ gap: 10 }}>
									<RgbaColorPickerPopover
										label="Color al pasar el ratón"
										value={normalizeColorToRgba(content.buttonImageHoverColor || "rgba(124, 58, 237, 1)")}
										onChange={(c) => effectiveUpdateElement({ ...el, content: { ...content, buttonImageHoverColor: c } })}
									/>
									<Box display="flex" alignItems="center" style={{ gap: 10 }}>
										<Box fontSize={12} fontWeight={600} color="#64748b" style={{ minWidth: 68 }}>Intensidad</Box>
										<Box fontSize={12} color="#64748b" style={{ minWidth: 42 }}>
											{Math.round((Number.isFinite(Number(content.buttonImageHoverOpacity)) ? Number(content.buttonImageHoverOpacity) : 0.45) * 100)}%
										</Box>
										<Slider
											value={(Number.isFinite(Number(content.buttonImageHoverOpacity)) ? Number(content.buttonImageHoverOpacity) : 0.45) * 100}
											onChange={(_, v) => effectiveUpdateElement({ ...el, content: { ...content, buttonImageHoverOpacity: v / 100 } })}
											min={0}
											max={100}
											style={{ color: "#7c3aed", flex: 1 }}
										/>
									</Box>
								</Box>
							)}
						</Box>
						<Box>
							<Box fontSize={12} fontWeight={600} color="#64748b" mb={0.5}>Ajuste de imagen</Box>
							<ToggleButtonGroup
								size="small"
								exclusive
								value={content.buttonImageFit === "contain" ? "contain" : "cover"}
								onChange={(_, val) => {
									if (val == null) return;
									effectiveUpdateElement({ ...el, content: { ...content, buttonImageFit: val } });
								}}
							>
								<ToggleButton value="cover" style={{ textTransform: "none" }}>Recortar</ToggleButton>
								<ToggleButton value="contain" style={{ textTransform: "none" }}>Contener</ToggleButton>
							</ToggleButtonGroup>
						</Box>
						<Box>
							<Box fontSize={12} fontWeight={600} color="#64748b" mb={0.5}>Esquinas (redondeo %)</Box>
							<Box fontSize={11} color="#64748b" mb={0.5}>{getButtonBorderRadiusPercent(style)}%</Box>
							<Slider
								value={getButtonBorderRadiusPercent(style)}
								onChange={(_, v) => effectiveUpdateElement({
									...el,
									style: { ...style, borderRadiusPercent: v, borderRadius: undefined },
								})}
								min={0}
								max={50}
								valueLabelDisplay="auto"
								valueLabelFormat={(v) => `${v}%`}
								color="primary"
							/>
						</Box>
						<Box p={1.5} borderRadius={10} style={{ background: "#fff", border: "1px solid #e2e8f0" }}>
							<Box fontSize={11} color="#64748b" mb={0.75}>Vista previa</Box>
							<Box style={{ width: "100%", height: 72, position: "relative" }}>
								<ButtonImageVisual content={content} style={style} isHovered={false} disabled={false} placeholder="Sin imagen" />
							</Box>
						</Box>
					</Box>
				) : null}
			</ErEditAccordion>

			{!imageMode ? (
			<>
			<ErEditAccordion title="Texto" icon={TextFieldsIcon} defaultOpen>
				<ErEditAccordion title="Texto" nested defaultOpen>
					<Box fontSize={11} color="#64748b" mb={1}>
						Doble clic en el botón del lienzo para editar el texto.
					</Box>
					<TextField
						placeholder="Ej: Empezar, Siguiente…"
						size="small"
						fullWidth
						variant="outlined"
						value={content.label || ""}
						onChange={(e) => effectiveUpdateElement({ ...el, content: { ...content, label: e.target.value } })}
					/>
				</ErEditAccordion>

				<ErEditAccordion title="Tipografía" nested defaultOpen>
					<Box display="flex" style={{ gap: 8, marginBottom: 10 }}>
						<FormControl size="small" style={{ flex: 1, minWidth: 0 }} variant="outlined">
							<InputLabel>Fuente</InputLabel>
							<Select
								value={style.fontFamily || "Arial, sans-serif"}
								onChange={(e) => {
									const v = e.target.value;
									if (v === ADD_FONT_OPTION) {
										onOpenFontManager();
										return;
									}
									effectiveUpdateElement({ ...el, style: { ...style, fontFamily: v } });
								}}
								label="Fuente"
							>
								{allFontOptions.map((f) => (
									<MenuItem key={f} value={f} style={{ fontFamily: f }}>
										{fontLabel(f)}
									</MenuItem>
								))}
								<MenuItem value={ADD_FONT_OPTION}>+ Añadir fuente</MenuItem>
							</Select>
						</FormControl>
						<DraftIntTextField
							label="Tamaño"
							min={8}
							max={120}
							style={{ flex: 1, minWidth: 0 }}
							value={style.fontSize ?? 16}
							onCommit={(n) => effectiveUpdateElement({ ...el, style: { ...style, fontSize: n } })}
						/>
					</Box>
					<Box mb={1.25} style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
						<RgbaColorPickerPopover
							label="Color del texto"
							value={normalizeColorToRgba(style.color)}
							onChange={(col) => effectiveUpdateElement({ ...el, style: { ...style, color: col } })}
						/>
					</Box>
					<Box display="flex" justifyContent="center" width="100%" mb={1}>
						<ToggleButtonGroup
							size="small"
							exclusive={false}
							value={[
								(style.fontWeight === "bold" && "bold") || null,
								(style.fontStyle === "italic" && "italic") || null,
								(style.textDecoration === "underline" && "underline") || null,
							].filter(Boolean)}
							onChange={(_, val) => {
								if (!val) return;
								const st = { ...style };
								st.fontWeight = val.includes("bold") ? "bold" : "normal";
								st.fontStyle = val.includes("italic") ? "italic" : "normal";
								st.textDecoration = val.includes("underline") ? "underline" : "none";
								effectiveUpdateElement({ ...el, style: st });
							}}
						>
							<ToggleButton value="bold" style={{ minWidth: 44, minHeight: 40 }}>
								<FormatBoldIcon style={{ fontSize: 18 }} />
							</ToggleButton>
							<ToggleButton value="italic" style={{ minWidth: 44, minHeight: 40 }}>
								<FormatItalicIcon style={{ fontSize: 18 }} />
							</ToggleButton>
							<ToggleButton value="underline" style={{ minWidth: 44, minHeight: 40 }}>
								<FormatUnderlinedIcon style={{ fontSize: 18 }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box fontSize={11} fontWeight={600} color="#64748b" mb={0.5}>
						Alineación
					</Box>
					<Box display="flex" justifyContent="center" width="100%">
						<ToggleButtonGroup
							size="small"
							exclusive
							value={style.textAlign || "center"}
							onChange={(_, val) => {
								if (val != null) effectiveUpdateElement({ ...el, style: { ...style, textAlign: val } });
							}}
						>
							<ToggleButton value="left" style={{ minWidth: 44, minHeight: 40 }}>
								<FormatAlignLeftIcon style={{ fontSize: 18 }} />
							</ToggleButton>
							<ToggleButton value="center" style={{ minWidth: 44, minHeight: 40 }}>
								<FormatAlignCenterIcon style={{ fontSize: 18 }} />
							</ToggleButton>
							<ToggleButton value="right" style={{ minWidth: 44, minHeight: 40 }}>
								<FormatAlignRightIcon style={{ fontSize: 18 }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				</ErEditAccordion>

				<ErEditAccordion title="Efecto visual" nested>
					<TextEffectPicker
						embedded
						effectStyle={pickEffectBundle(style, "text")}
						fallbackColor={style.color}
						onEffectStyleChange={patchTextEffect}
					/>
				</ErEditAccordion>
			</ErEditAccordion>

			<ErEditAccordion title="Icono" icon={ImageIcon} defaultOpen={!!content.icon}>
				<ErEditAccordion title="Icono" nested defaultOpen>
					<Box display="flex" alignItems="center" style={{ gap: 8 }}>
						<Box
							onClick={onOpenIconPicker}
							display="flex"
							alignItems="center"
							justifyContent="center"
							style={{
								width: 52,
								height: 52,
								borderRadius: 10,
								flexShrink: 0,
								border: hasIcon ? "2px solid #e2e8f0" : "2px dashed #cbd5e1",
								background: hasIcon ? "#fff" : "transparent",
								cursor: "pointer",
							}}
						>
							{hasIcon ? (
								React.createElement(BUTTON_ICON_MAP[content.icon], { style: { fontSize: 26, color: "#475569" } })
							) : (
								<StarBorderIcon style={{ fontSize: 24, color: "#94a3b8" }} />
							)}
						</Box>
						<Box flex={1} minWidth={0} display="flex" flexDirection="column" style={{ gap: 6 }}>
							<Button size="small" variant="outlined" onClick={onOpenIconPicker} style={{ textTransform: "none" }} startIcon={<StarBorderIcon style={{ fontSize: 16 }} />}>
								{content.icon ? "Cambiar icono" : "Añadir icono"}
							</Button>
							{content.icon && (
								<Button
									size="small"
									onClick={() => effectiveUpdateElement({ ...el, content: { ...content, icon: "" } })}
									style={{ textTransform: "none", color: "#64748b" }}
									startIcon={<ClearIcon style={{ fontSize: 16 }} />}
								>
									Quitar icono
								</Button>
							)}
						</Box>
					</Box>
				</ErEditAccordion>

				{content.icon && (
					<ErEditAccordion title="Posición y tamaño" nested defaultOpen>
						<Box fontSize={11} fontWeight={600} color="#64748b" mb={0.5}>
							Posición respecto al texto
						</Box>
						<ToggleButtonGroup
							size="small"
							exclusive
							value={content.iconPosition || "before"}
							onChange={(_, val) => {
								if (val != null) effectiveUpdateElement({ ...el, content: { ...content, iconPosition: val } });
							}}
							style={{ marginBottom: 10 }}
						>
							<ToggleButton value="before" style={{ minWidth: 44, minHeight: 36 }}>
								<FormatAlignLeftIcon style={{ fontSize: 18 }} />
							</ToggleButton>
							<ToggleButton value="after" style={{ minWidth: 44, minHeight: 36 }}>
								<FormatAlignRightIcon style={{ fontSize: 18 }} />
							</ToggleButton>
						</ToggleButtonGroup>
						<Box fontSize={11} fontWeight={600} color="#64748b" mb={0.5}>
							Tamaño del icono
						</Box>
						<ToggleButtonGroup
							size="small"
							exclusive
							value={String(content.iconSize ?? 1.2)}
							onChange={(_, val) => {
								if (val != null) effectiveUpdateElement({ ...el, content: { ...content, iconSize: Number(val) } });
							}}
						>
							<ToggleButton value="0.8" style={{ minWidth: 36, minHeight: 34 }}>S</ToggleButton>
							<ToggleButton value="1" style={{ minWidth: 36, minHeight: 34 }}>M</ToggleButton>
							<ToggleButton value="1.2" style={{ minWidth: 36, minHeight: 34 }}>L</ToggleButton>
							<ToggleButton value="1.5" style={{ minWidth: 36, minHeight: 34 }}>XL</ToggleButton>
						</ToggleButtonGroup>
					</ErEditAccordion>
				)}

				<ErEditAccordion title="Efecto visual" nested>
					<TextEffectPicker
						embedded
						effectStyle={pickEffectBundle(content, "icon")}
						fallbackColor={style.color}
						onEffectStyleChange={patchIconEffect}
					/>
				</ErEditAccordion>
			</ErEditAccordion>

			<ErEditAccordion title="Apariencia del botón" icon={PaletteIcon}>
				<ErEditAccordion title="Colores" nested defaultOpen>
					<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
						<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
							<RgbaColorPickerPopover
								label="Color de fondo"
								value={normalizeColorToRgba(style.backgroundColor, 0)}
								onChange={(color) => effectiveUpdateElement({ ...el, style: { ...style, backgroundColor: color } })}
							/>
						</Box>
						<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
							<RgbaColorPickerPopover
								label="Al pasar el ratón"
								value={normalizeColorToRgba(
									style.hoverBackgroundColor || getDefaultHoverColor(style.backgroundColor || "rgba(124, 58, 237, 1)"),
									1
								)}
								onChange={(color) => effectiveUpdateElement({ ...el, style: { ...style, hoverBackgroundColor: color } })}
							/>
						</Box>
					</Box>
				</ErEditAccordion>

				<ErEditAccordion title="Forma" nested defaultOpen>
					<Box fontSize={11} fontWeight={600} color="#64748b" mb={0.5}>
						Esquinas (redondeo %)
					</Box>
					<Box fontSize={11} color="#64748b" mb={0.5}>{getButtonBorderRadiusPercent(style)}%</Box>
					<Slider
						value={getButtonBorderRadiusPercent(style)}
						onChange={(_, v) => effectiveUpdateElement({
							...el,
							style: { ...style, borderRadiusPercent: v, borderRadius: undefined },
						})}
						min={0}
						max={50}
						valueLabelDisplay="auto"
						valueLabelFormat={(v) => `${v}%`}
						color="primary"
					/>
				</ErEditAccordion>

				<ErEditAccordion title="Marco" nested>
					<Box fontSize={12} color="#64748b" lineHeight={1.45} mb={1.25}>
						Borde alrededor del botón. Deja el grosor vacío para el estilo automático del editor; 0 = sin marco; 1 o más = marco visible.
					</Box>
					<Box display="flex" flexDirection="column" style={{ gap: 12 }}>
						<TextField
							label="Grosor del borde (px)"
							type="number"
							size="small"
							fullWidth
							variant="outlined"
							placeholder="Automático"
							inputProps={{ min: 0, max: 40 }}
							value={style.borderWidth != null ? style.borderWidth : ""}
							onChange={(e) => {
								const t = String(e.target.value ?? "").trim();
								if (t === "") {
									const { borderWidth, ...restStyle } = style;
									effectiveUpdateElement({ ...el, style: restStyle });
									return;
								}
								const n = parseInt(t, 10);
								if (!Number.isNaN(n)) {
									effectiveUpdateElement({ ...el, style: { ...style, borderWidth: Math.min(40, Math.max(0, n)) } });
								}
							}}
						/>
						<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
							<RgbaColorPickerPopover
								label="Color del borde"
								value={normalizeColorToRgba(style.borderColor || "rgba(124, 58, 237, 1)")}
								onChange={(color) => effectiveUpdateElement({ ...el, style: { ...style, borderColor: color } })}
							/>
						</Box>
						<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
							<RgbaColorPickerPopover
								label="Color del borde (al pasar el ratón)"
								value={normalizeColorToRgba(style.hoverBorderColor, 0)}
								onChange={(color) => effectiveUpdateElement({ ...el, style: { ...style, hoverBorderColor: color } })}
							/>
						</Box>
					</Box>
				</ErEditAccordion>

				<ErEditAccordion title="Efecto del marco" nested>
					<TextEffectPicker
						embedded
						effectStyle={pickEffectBundle(style, "frame")}
						fallbackColor={style.color}
						onEffectStyleChange={patchFrameEffect}
					/>
				</ErEditAccordion>
			</ErEditAccordion>
			</>
			) : null}
		</>
	);
}
